import { lazy } from 'react';
import { Routes, Route } from 'react-router-dom';
import { PrivateRoute } from '@/components/private-route';
import { AppLayout } from '@/components/app-layout';
import { GymLayout } from '@/components/gym-layout';

// Public Pages
const HomePage = lazy(() => import('@/pages/home-page'));
const SignInPage = lazy(() => import('@/pages/auth/sign-in'));
const SignUpPage = lazy(() => import('@/pages/auth/sign-up'));
const VerifyEmailOtpPage = lazy(() => import('@/pages/auth/verify-email-otp'));
const ForgotPasswordPage = lazy(() => import('@/pages/auth/forgot-password'));
const ForgotPasswordOtpPage = lazy(() => import('@/pages/auth/forgot-password-otp'));
const NewPasswordPage = lazy(() => import('@/pages/auth/new-password'));
const NotFoundPage = lazy(() => import('@/pages/not-found-page'));

// User Pages (Private)
const OnboardingPage = lazy(() => import('@/pages/user/onboarding'));

// App Pages (Private)
const NewGymPage = lazy(() => import('@/pages/app/gym/new'));
const GymFilesPage = lazy(() => import('@/pages/app/gym/files'));
const GymDetailsPage = lazy(() => import('@/pages/app/gym/details'));
const GymHomePage = lazy(() => import('@/pages/app/gym/dashboard'));
const SettingsPage = lazy(() => import('@/pages/app/settings'));
const GymNotificationsPage = lazy(() => import('@/pages/app/gym/notifications'));

// Manage Gyms page
const ManageGymsPage = lazy(() => import('@/pages/app/gym/manage-gyms'));

// Placeholder pages
const PaymentsPage = lazy(() =>
  import('@/pages/app/placeholder-page').then((m) => ({ default: m.PaymentsPage }))
);
const HelpPage = lazy(() =>
  import('@/pages/app/placeholder-page').then((m) => ({ default: m.HelpPage }))
);
const TermsPage = lazy(() =>
  import('@/pages/app/placeholder-page').then((m) => ({ default: m.TermsPage }))
);
const PrivacyPage = lazy(() =>
  import('@/pages/app/placeholder-page').then((m) => ({ default: m.PrivacyPage }))
);
const EarningsPage = lazy(() =>
  import('@/pages/app/placeholder-page').then((m) => ({ default: m.EarningsPage }))
);

const SlotManagerPage = lazy(() => import('@/pages/slot-manager'));
const AddSlotPage = lazy(() => import('@/pages/slot-manager/add-slot'));
const MembersPage = lazy(() => import('@/pages/app/gym/members'));
const MemberOverviewPage = lazy(() => import('@/pages/app/gym/member-overview'));
const AddMemberPage = lazy(() => import('@/pages/app/gym/add-member'));

export function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<HomePage />} />
      <Route path="/auth/signin" element={<SignInPage />} />
      <Route path="/auth/signup" element={<SignUpPage />} />
      <Route path="/auth/verify-email" element={<VerifyEmailOtpPage />} />
      <Route path="/auth/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/auth/forgot-password-otp" element={<ForgotPasswordOtpPage />} />
      <Route path="/auth/new-password" element={<NewPasswordPage />} />

      {/* Private: User Routes */}
      <Route
        path="/user/onboarding"
        element={
          <PrivateRoute>
            <OnboardingPage />
          </PrivateRoute>
        }
      />

      {/* Private: App Routes - New Gym (no gymId yet) */}
      <Route
        path="/app/gym/new"
        element={
          <PrivateRoute>
            <AppLayout>
              <NewGymPage />
            </AppLayout>
          </PrivateRoute>
        }
      />

      {/* Private: Global App Routes (not gym-specific) */}
      <Route
        path="/app/help"
        element={
          <PrivateRoute>
            <AppLayout>
              <HelpPage />
            </AppLayout>
          </PrivateRoute>
        }
      />
      <Route
        path="/app/terms"
        element={
          <PrivateRoute>
            <AppLayout>
              <TermsPage />
            </AppLayout>
          </PrivateRoute>
        }
      />
      <Route
        path="/app/privacy"
        element={
          <PrivateRoute>
            <AppLayout>
              <PrivacyPage />
            </AppLayout>
          </PrivateRoute>
        }
      />

      {/* Private: Gym-specific Routes without bottom nav */}
      <Route
        path="/app/:gymId/members"
        element={
          <PrivateRoute>
            <AppLayout>
              <MembersPage />
            </AppLayout>
          </PrivateRoute>
        }
      />
      <Route
        path="/app/:gymId/members/:memberId"
        element={
          <PrivateRoute>
            <AppLayout>
              {/* TODO: Create MemberDetailsPage */}
              <MembersPage />
            </AppLayout>
          </PrivateRoute>
        }
      />
      <Route
        path="/app/:gymId/settings"
        element={
          <PrivateRoute>
            <AppLayout>
              <SettingsPage />
            </AppLayout>
          </PrivateRoute>
        }
      />
      <Route
        path="/app/:gymId/settings/manage-gyms"
        element={
          <PrivateRoute>
            <AppLayout>
              <ManageGymsPage />
            </AppLayout>
          </PrivateRoute>
        }
      />
      <Route
        path="/app/:gymId/settings/members"
        element={
          <PrivateRoute>
            <AppLayout>
              <MemberOverviewPage />
            </AppLayout>
          </PrivateRoute>
        }
      />
      <Route
        path="/app/:gymId/settings/members/new"
        element={
          <PrivateRoute>
            <AppLayout>
              <AddMemberPage />
            </AppLayout>
          </PrivateRoute>
        }
      />
      <Route
        path="/app/:gymId/settings/details"
        element={
          <PrivateRoute>
            <AppLayout>
              <GymDetailsPage />
            </AppLayout>
          </PrivateRoute>
        }
      />
      <Route
        path="/app/:gymId/payments"
        element={
          <PrivateRoute>
            <AppLayout>
              <PaymentsPage />
            </AppLayout>
          </PrivateRoute>
        }
      />

      {/* Private: Gym Files/Assets Upload */}
      <Route
        path="/app/:gymId/files"
        element={
          <PrivateRoute>
            <AppLayout>
              <GymFilesPage />
            </AppLayout>
          </PrivateRoute>
        }
      />

      {/* Private: Gym Routes with bottom nav (use GymLayout) */}
      <Route
        path="/app/:gymId"
        element={
          <PrivateRoute>
            <GymLayout />
          </PrivateRoute>
        }
      >
        <Route path="home" element={<GymHomePage />} />
        <Route path="earnings" element={<EarningsPage />} />
        <Route path="notifications" element={<GymNotificationsPage />} />
        <Route path="slot-manager" element={<SlotManagerPage />} />
      </Route>

      {/* Private: Slot Manager - Add New Slot (no bottom nav) */}
      <Route
        path="/app/:gymId/slot-manager/new"
        element={
          <PrivateRoute>
            <AppLayout>
              <AddSlotPage />
            </AppLayout>
          </PrivateRoute>
        }
      />

      {/* 404 */}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}
